import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { switchLocale } from '@/i18n';
import { Routes } from '@/router';
import type { RouteDefinition } from '@/router/route-config.ts';
import type { LoginResponse } from '@/schemas/auth';
import {
  LoginResponseSchema,
  UserSchema,
  type Customer,
  type Employee,
  type LoginCredentials,
  type User,
} from '@/schemas/auth';
import { tokenManager, type UserType } from '@/services/tokenManager';
import { API_ROUTES, ApiError, apiRequest, validatedApiRequest } from '@/types/api-routes';
import { validateApiResponse } from '@/utils/validation';

export const useAuthStore = defineStore('auth', () => {
  const employeeUser = ref<Employee | null>(null);
  const customerUser = ref<Customer | null>(null);
  const currentContext = ref<UserType | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isEmployee = computed(() => !!employeeUser.value);
  const isCustomer = computed(() => !!customerUser.value);

  const currentUser = computed<User | null>(() => {
    if (currentContext.value === 'employee') {
      return employeeUser.value;
    }
    if (currentContext.value === 'customer') {
      return customerUser.value;
    }
    return customerUser.value || employeeUser.value;
  });

  const isAuthenticated = computed(() => !!currentUser.value);

  const isLoading = computed(() => loading.value);

  async function loginEmployee(credentials: LoginCredentials) {
    loading.value = true;
    error.value = null;

    const result = await validatedApiRequest<LoginResponse>(API_ROUTES.auth.login, LoginResponseSchema, {
      ...credentials,
      context: 'admin',
    });

    loading.value = false;

    if (!result.ok) {
      const errorMessage = result.error instanceof ApiError ? result.error.message : 'An error occurred during login';
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    }

    const loginResponse = result.data;

    if (loginResponse.user_type !== 'employee') {
      error.value = 'This email is not registered as an employee account.';
      return { success: false, error: error.value };
    }

    if (!loginResponse.requires_2fa && loginResponse.user) {
      employeeUser.value = loginResponse.user as Employee;
      currentContext.value = 'employee';

      if (employeeUser.value.locale) {
        await switchLocale(employeeUser.value.locale);
      }
    }

    tokenManager.setEmployeeToken(loginResponse.token);

    return { success: true, requires_2fa: loginResponse.requires_2fa };
  }

  async function loginCustomer(credentials: LoginCredentials) {
    loading.value = true;
    error.value = null;

    const result = await apiRequest<unknown>(API_ROUTES.auth.login, {
      ...credentials,
      context: 'customer',
    });

    loading.value = false;

    if (!result.ok) {
      const errorMessage = result.error instanceof ApiError ? result.error.message : 'An error occurred during login';
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    }

    const loginResponse = validateApiResponse(result.data, LoginResponseSchema);

    if (loginResponse.user_type !== 'customer') {
      error.value = 'This email is not registered as a customer account.';
      return { success: false, error: error.value };
    }

    tokenManager.setCustomerToken(loginResponse.token);

    if (!loginResponse.requires_2fa) {
      if (loginResponse.user) {
        customerUser.value = loginResponse.user as Customer;
      } else {
        await fetchCustomerUser();
      }

      currentContext.value = 'customer';

      if (customerUser.value?.locale) {
        await switchLocale(customerUser.value.locale);
      }
    }

    return {
      success: true,
      requires_2fa: loginResponse.requires_2fa,
      requires_company_selection: loginResponse.requires_company_selection,
    };
  }

  async function fetchEmployeeUser() {
    const token = tokenManager.getEmployeeToken();
    if (!token) {
      return;
    }

    loading.value = true;

    const result = await apiRequest<User>(API_ROUTES.auth.user.show);

    loading.value = false;

    if (!result.ok) {
      console.error('Failed to fetch employee user:', result.error);
      tokenManager.clearEmployeeToken();
      employeeUser.value = null;
      return;
    }

    let user: User;
    try {
      user = validateApiResponse(result.data, UserSchema);
    } catch (error) {
      console.error('Employee user validation failed, clearing token:', error);
      tokenManager.clearEmployeeToken();
      employeeUser.value = null;
      return;
    }

    if (user.user_type !== 'employee') {
      console.warn('Employee token returned a non-employee user. Clearing token.');
      tokenManager.clearEmployeeToken();
      employeeUser.value = null;
      return;
    }

    employeeUser.value = user as Employee;

    if (employeeUser.value.locale) {
      await switchLocale(employeeUser.value.locale);
    }
  }

  async function fetchCustomerUser() {
    const token = tokenManager.getCustomerToken();
    if (!token) {
      return;
    }

    loading.value = true;

    const result = await apiRequest<User>(API_ROUTES.auth.user.show);

    loading.value = false;

    if (!result.ok) {
      console.error('Failed to fetch customer user:', result.error);
      tokenManager.clearCustomerToken();
      customerUser.value = null;
      return;
    }

    let user: User;
    try {
      user = validateApiResponse(result.data, UserSchema);
    } catch (error) {
      console.error('Customer user validation failed, clearing token:', error);
      tokenManager.clearCustomerToken();
      customerUser.value = null;
      return;
    }

    if (user.user_type !== 'customer') {
      console.warn('Customer token returned a non-customer user. Clearing token.');
      tokenManager.clearCustomerToken();
      customerUser.value = null;
      return;
    }

    const existingPermissions = customerUser.value?.permissions;
    const existingRoles = customerUser.value?.roles;
    customerUser.value = {
      ...(user as Customer),
      permissions: (user as Customer).permissions ?? existingPermissions ?? [],
      roles: (user as Customer).roles ?? existingRoles ?? [],
    };

    if (customerUser.value.locale) {
      await switchLocale(customerUser.value.locale);
    }
  }

  async function logoutEmployee() {
    try {
      const token = tokenManager.getEmployeeToken();
      if (token) {
        const result = await apiRequest(API_ROUTES.auth.logout);
        if (!result.ok) {
          console.error('Logout error:', result.error);
        }
      }
    } catch (err) {
      console.error('Employee logout error:', err);
    } finally {
      tokenManager.clearEmployeeToken();
      employeeUser.value = null;
      if (currentContext.value === 'employee') {
        currentContext.value = isCustomer.value ? 'customer' : null;
      }
    }
  }

  async function logoutCustomer() {
    try {
      const token = tokenManager.getCustomerToken();
      if (token) {
        const result = await apiRequest(API_ROUTES.auth.logout);
        if (!result.ok) {
          console.error('Logout error:', result.error);
        }
      }
    } catch (err) {
      console.error('Customer logout error:', err);
    } finally {
      tokenManager.clearCustomerToken();
      customerUser.value = null;
      if (currentContext.value === 'customer') {
        currentContext.value = isEmployee.value ? 'employee' : null;
      }
    }
  }

  async function logoutAll() {
    await Promise.all([logoutEmployee(), logoutCustomer()]);
    currentContext.value = null;
  }

  function setContext(context: UserType) {
    currentContext.value = context;
  }

  function clearError() {
    error.value = null;
  }

  async function logout() {
    if (currentContext.value === 'employee') {
      await logoutEmployee();
    } else if (currentContext.value === 'customer') {
      await logoutCustomer();
    } else if (customerUser.value) {
      await logoutCustomer();
    } else if (employeeUser.value) {
      await logoutEmployee();
    }
  }

  async function fetchUser() {
    if (currentContext.value === 'employee') {
      await fetchEmployeeUser();
    } else if (currentContext.value === 'customer') {
      await fetchCustomerUser();
    } else if (tokenManager.getCustomerToken()) {
      await fetchCustomerUser();
    } else if (tokenManager.getEmployeeToken()) {
      await fetchEmployeeUser();
    }
  }

  function getHomeRoute(): RouteDefinition {
    if (currentContext.value === 'employee') {
      return Routes.Admin.Dashboard;
    }
    if (currentContext.value === 'customer' || customerUser.value) {
      return Routes.Customer.Dashboard;
    }
    if (employeeUser.value) {
      return Routes.Admin.Dashboard;
    }
    return Routes.Customer.Login;
  }

  function getLoginRoute(): RouteDefinition {
    if (currentContext.value === 'employee') {
      return Routes.Admin.Login;
    }
    if (currentContext.value === 'customer') {
      return Routes.Customer.Login;
    }
    if (customerUser.value) {
      return Routes.Customer.Login;
    }
    if (employeeUser.value) {
      return Routes.Admin.Login;
    }
    return Routes.Customer.Login;
  }

  let initAuthPromise: Promise<void> | null = null;

  async function initAuth() {
    if (initAuthPromise) {
      return initAuthPromise;
    }

    initAuthPromise = performInitAuth();

    try {
      await initAuthPromise;
    } finally {
      initAuthPromise = null;
    }
  }

  async function performInitAuth() {
    const employeeToken = tokenManager.getEmployeeToken();
    const customerToken = tokenManager.getCustomerToken();

    const promises = [];

    if (employeeToken && !employeeUser.value) {
      promises.push(fetchEmployeeUser());
    }

    if (customerToken && !customerUser.value) {
      promises.push(fetchCustomerUser());
    }

    await Promise.all(promises);

    const currentPath = window.location.pathname;
    const pathUserType = tokenManager.getUserTypeFromPath(currentPath);

    if (pathUserType) {
      currentContext.value = pathUserType;
    } else if (customerUser.value) {
      currentContext.value = 'customer';
    } else if (employeeUser.value) {
      currentContext.value = 'employee';
    }
  }

  function updateCustomerPermissions(permissions: string[], roles: string[]) {
    if (customerUser.value) {
      customerUser.value = {
        ...customerUser.value,
        permissions,
        roles,
      };
    }
  }

  return {
    employeeUser,
    customerUser,
    currentContext,
    loading,
    error,

    isEmployee,
    isCustomer,
    currentUser,
    isAuthenticated,
    isLoading,

    loginEmployee,
    loginCustomer,
    fetchEmployeeUser,
    fetchCustomerUser,
    logoutEmployee,
    logoutCustomer,
    logoutAll,
    logout,
    fetchUser,
    getHomeRoute,
    getLoginRoute,
    setContext,
    clearError,
    initAuth,
    updateCustomerPermissions,
  };
});
